import {
  AssistantCompletionRequest,
  AssistantCompletionResponse,
  AssistantDocsPage,
  AssistantDocsPageContent,
  AssistantFeedbackRequest,
  AssistantMissingCapabilityReport,
} from '@paragon/contract-library';

import { DesignService } from '../connections';

/**
 * Asks the Assistant's model for its next step. `onText` gets the reply's text as the model writes it; the whole reply
 * comes back at the end.
 */
export async function complete(
  request: AssistantCompletionRequest,
  onText?: (text: string) => void,
  cancellationPromise?: PromiseLike<void>,
) {
  const body = await DesignService.post<ReadableStream<Uint8Array>>('assistant/completions', {
    data: request,
    responseType: 'stream',
    cancellationPromise,
  });
  return readCompletionStream(body, onText);
}

/**
 * Reads DesignServer's stream of server-sent events for a completion: `text` events as the model writes, then `done`
 * with the whole reply, or `error` with the status and detail the request would have failed with, thrown as the
 * problem it describes.
 */
export async function readCompletionStream(
  body: ReadableStream<Uint8Array>,
  onText?: (text: string) => void,
): Promise<AssistantCompletionResponse> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffered += decoder.decode(value, { stream: true });

    let end;
    while ((end = buffered.indexOf('\n\n')) >= 0) {
      const event = parseEvent(buffered.slice(0, end));
      buffered = buffered.slice(end + 2);
      if (event == null) continue;

      switch (event.name) {
        case 'text':
          onText?.((event.data as { text: string }).text);
          break;
        case 'done':
          void reader.cancel();
          return event.data as AssistantCompletionResponse;
        case 'error': {
          void reader.cancel();
          const { status, detail } = event.data as { status: number; detail: string };
          throw { type: 'about:blank', title: 'Error', status, detail };
        }
      }
    }
  }
  throw { type: 'about:blank', title: 'Error', status: 502, detail: 'The reply ended before the model finished.' };
}

/** An event's name and data, or `null` for a comment, such as the ones that keep the stream open. */
function parseEvent(block: string): { name: string; data: unknown } | null {
  let name = 'message';
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) name = line.slice('event:'.length).trim();
    else if (line.startsWith('data:')) data.push(line.slice('data:'.length).trimStart());
  }
  return data.length === 0 ? null : { name, data: JSON.parse(data.join('\n')) };
}

export function sendFeedback(feedback: AssistantFeedbackRequest) {
  return DesignService.post<void>('assistant/feedback', { data: feedback });
}

/** Searches Paragon Docs, giving the best-matching pages first. */
export function searchDocs(query: string) {
  return DesignService.get<AssistantDocsPage[]>('assistant/docs', { params: new URLSearchParams({ query }) });
}

/** The whole of a Paragon Docs page, by the link a search gave. */
export function readDocsPage(link: string) {
  return DesignService.get<AssistantDocsPageContent>('assistant/docs/page', { params: new URLSearchParams({ link }) });
}

/** Tells the team about something the Assistant or an Agent had no tool to do. */
export function reportMissingCapability(report: AssistantMissingCapabilityReport) {
  return DesignService.post<void>('assistant/missingCapabilities', { data: report });
}
